import {
  ButtonDefinition,
  CargoTracking,
  ChatAddBlock,
  ChatAddress,
  ChatAddressKind,
  ChatBalance,
  ChatBankAccount,
  ChatBrand,
  ChatBrandCompany,
  ChatCategory,
  ChatCommissionPaymentType,
  ChatCommissionRate,
  ChatCoupon,
  ChatSavedCreditCard,
  ChatCommissionRates,
  ChatBasketCommissionRates,
  ChatBasketRateTab,
  ChatBasketRateScope,
  ChatBasketRateRow,
  ChatBasketProposal,
  ChatBasketProposalExcluded,
  ChatBasketProposalLine,
  ChatCurrency,
  ChatCompany,
  ChatDebtStatus,
  ChatPosLimits,
  ChatExternalLink,
  ChatForm,
  ChatInvoice,
  ChatMessageResponse,
  ChatMoney,
  ChatNavigationAction,
  ChatPromoVideo,
  ChatOrder,
  ChatOrderCargoLine,
  ChatProduct,
  ChatProductCampaign,
  ChatProductSearch,
  ChatRepresentativeCompany,
  ChatRepresentativeContact,
  ChatServiceProduct,
  ChatTable,
  CommissionMode,
  GuaranteeDetail,
  SerialNumberCheck,
} from './types'

type UnknownRecord = Record<string, unknown>

function asRecord(value: unknown): UnknownRecord | undefined {
  return value && typeof value === 'object' && !Array.isArray(value) ? (value as UnknownRecord) : undefined
}

function pick(source: unknown, ...keys: string[]): unknown {
  const record = asRecord(source)
  if (!record) return undefined

  for (const key of keys) {
    const value = record[key]
    if (value !== undefined && value !== null) return value
  }
  return undefined
}

function toString(value: unknown, fallback = ''): string {
  return value === undefined || value === null ? fallback : String(value)
}

function toNullableString(value: unknown): string | null {
  const normalized = toString(value).trim()
  return normalized ? normalized : null
}

function toNumber(value: unknown, fallback = 0): number {
  const normalized = Number(value)
  return Number.isFinite(normalized) ? normalized : fallback
}

function toNullableNumber(value: unknown): number | null {
  if (value === undefined || value === null || value === '') return null
  const normalized = Number(value)
  return Number.isFinite(normalized) ? normalized : null
}

export function normalizeCurrency(value: unknown): ChatOrder['currency'] {
  const currency = toString(value, 'TRY').toUpperCase()
  return currency === 'USD' || currency === 'EUR' ? currency : 'TRY'
}

/**
 * Faz-44: Yurtiçi takip bağlantısı yalnız bu sabit önekle kabul edilir (PROMO_VIDEO_URLS deseni).
 * Widget adres kurmaz; sunucunun kurduğu değer birebir önekle başlamıyorsa ya da önekten sonra
 * takip numarası yoksa null'a düşer ve buton çizilmez.
 */
export const YURTICI_TRACKING_URL_PREFIX = 'https://www.yurticikargo.com/tr/online-servisler/gonderi-sorgula?code='

export function normalizeYurticiTrackingUrl(value: unknown): string | null {
  const url = toNullableString(value)
  if (!url || !url.startsWith(YURTICI_TRACKING_URL_PREFIX)) return null
  return url.length > YURTICI_TRACKING_URL_PREFIX.length ? url : null
}

function normalizeOrderCargoLine(value: unknown): ChatOrderCargoLine | null {
  const text = toNullableString(pick(value, 'text', 'Text'))
  if (!text) return null

  return {
    deliveryNumber: toNullableString(pick(value, 'deliveryNumber', 'DeliveryNumber')),
    text,
    date: toNullableString(pick(value, 'date', 'Date')),
    trackingUrl: normalizeYurticiTrackingUrl(pick(value, 'trackingUrl', 'TrackingUrl')),
  }
}

function normalizeOrderCargo(value: unknown): ChatOrderCargoLine[] | null {
  if (!Array.isArray(value)) return null
  return value.map(normalizeOrderCargoLine).filter((line): line is ChatOrderCargoLine => line !== null)
}

export function normalizeOrder(value: unknown): ChatOrder {
  return {
    orderNumber: toString(pick(value, 'orderNumber', 'OrderNumber', 'SIPARIS_BELGENO')),
    companyCode: toNullableString(pick(value, 'companyCode', 'CompanyCode', 'COMPANY_CODE')) ?? undefined,
    deliveryNumber: toNullableString(pick(value, 'deliveryNumber', 'DeliveryNumber', 'TES_VBELN')),
    orderDate: toString(pick(value, 'orderDate', 'OrderDate', 'SIPARIS_TARIHI')),
    amount: toNumber(pick(value, 'amount', 'Amount', 'NET_TUTAR')),
    currency: normalizeCurrency(pick(value, 'currency', 'Currency', 'DOVIZ_KODU')),
    invoiceNumber: toNullableString(pick(value, 'invoiceNumber', 'InvoiceNumber', 'FATURA_BELGENO')) ?? undefined,
    eInvoiceNumber: toNullableString(pick(value, 'eInvoiceNumber', 'EInvoiceNumber', 'EFATNO')) ?? undefined,
    invoiceDate: toNullableString(pick(value, 'invoiceDate', 'InvoiceDate', 'FATURA_TARIHI')) ?? undefined,
    cargo: normalizeOrderCargo(pick(value, 'cargo', 'Cargo')),
  }
}

/** Banka hesabı satırı. IBAN yalnız boşluklardan arındırılır — doğrulanmaz, biçimi değiştirilmez. */
export function normalizeBankAccount(value: unknown): ChatBankAccount {
  // Faz-42: kısa ad yalnız geldiyse taşınır; eski yükün şekli değişmez.
  const companyShortName = toNullableString(pick(value, 'companyShortName', 'CompanyShortName')) ?? undefined
  return {
    companyName: toString(pick(value, 'companyName', 'CompanyName', 'Company')),
    ...(companyShortName ? { companyShortName } : {}),
    bankName: toString(pick(value, 'bankName', 'BankName', 'Bank')),
    currency: normalizeCurrency(pick(value, 'currency', 'Currency', 'DOVIZ_KODU')),
    iban: toString(pick(value, 'iban', 'Iban', 'IBAN')).replace(/\s/g, '').toUpperCase(),
  }
}

/** Adres türü: sözleşme "invoice"|"shipping". Tanınmayan değer sevk sayılır — bir sevk adresi yanlışlıkla "fatura adresiniz" olarak etiketlenmesin. */
export function normalizeAddressKind(value: unknown): ChatAddressKind {
  return toString(value).trim().toLowerCase() === 'invoice' ? 'invoice' : 'shipping'
}

/** Adres satırı. Metin alanları yalnız taşınır — biçim değiştirilmez, adres doğrulanmaz. */
export function normalizeAddress(value: unknown): ChatAddress {
  return {
    kind: normalizeAddressKind(pick(value, 'kind', 'Kind')),
    title: toNullableString(pick(value, 'title', 'Title')),
    contactName: toNullableString(pick(value, 'contactName', 'ContactName')),
    addressLine: toString(pick(value, 'addressLine', 'AddressLine')).trim(),
    neighbourhood: toNullableString(pick(value, 'neighbourhood', 'Neighbourhood')),
    district: toNullableString(pick(value, 'district', 'District')),
    city: toNullableString(pick(value, 'city', 'City')),
    postCode: toNullableString(pick(value, 'postCode', 'PostCode')),
    sevkCode: toNullableString(pick(value, 'sevkCode', 'SevkCode')),
    phone: toNullableString(pick(value, 'phone', 'Phone')),
    email: toNullableString(pick(value, 'email', 'Email')),
    isPreferred: Boolean(pick(value, 'isPreferred', 'IsPreferred')),
  }
}

/** Kampanya kuponu satırı. Kod zorunlu; ad/açıklama boş kalabilir (kart dolu alanları çizer). */
export function normalizeCoupon(value: unknown): ChatCoupon {
  return {
    code: toString(pick(value, 'code', 'Code', 'giftCode', 'GiftCode')).trim(),
    campaignName: toNullableString(pick(value, 'campaignName', 'CampaignName')),
    description: toNullableString(pick(value, 'description', 'Description', 'campaignDescription', 'CampaignDescription')),
  }
}

/**
 * Servisteki ürün kaydı (arıza takip). Durum METİN olarak taşınır: sayısal `Durum` kodu bilerek
 * okunmaz, koddan durum metni uydurulmaz. `Kur` anahtarı da kullanılmaz — MyReturns modelinde
 * döviz *kodu*, bot metninde ise *oran* anlamına geliyor; aynı ada iki anlam bağlanmaz.
 */
export function normalizeServiceProduct(value: unknown): ChatServiceProduct {
  return {
    productName: toString(pick(value, 'productName', 'ProductName', 'MalzemeAdi')).trim(),
    serialNumber: toNullableString(pick(value, 'serialNumber', 'SerialNumber', 'SeriNo')),
    status: toNullableString(pick(value, 'status', 'Status', 'DurumAciklama')),
    formNumber: toNullableString(pick(value, 'formNumber', 'FormNumber', 'TakipFormNo')),
    unitPrice: toNullableNumber(pick(value, 'unitPrice', 'UnitPrice', 'BirimFiyati')),
    currency: normalizeCurrency(pick(value, 'currency', 'Currency', 'ParaBirimi', 'DOVIZ_KODU')),
    exchangeRate: toNullableNumber(pick(value, 'exchangeRate', 'ExchangeRate', 'KurDegeri')),
  }
}

/**
 * Kayıtlı kredi kartı satırı. Kart numarası backend'de maskelendiği için burada YALNIZ taşınır —
 * maskeleme/kırpma yapılmaz ki gösterilen değer portaldakiyle birebir aynı olsun.
 */
export function normalizeSavedCreditCard(value: unknown): ChatSavedCreditCard {
  return {
    bankName: toString(pick(value, 'bankName', 'BankName')).trim(),
    maskedNumber: toString(pick(value, 'maskedNumber', 'MaskedNumber', 'cardNumber', 'CardNumber')).trim(),
    cardHolder: toNullableString(pick(value, 'cardHolder', 'CardHolder', 'cardName', 'CardName')),
    kvkkText: toString(pick(value, 'kvkkText', 'KvkkText')).trim(),
    kvkkApproved: Boolean(pick(value, 'kvkkApproved', 'KvkkApproved')),
    statusText: toNullableString(pick(value, 'statusText', 'StatusText')),
    addedBy: toNullableString(pick(value, 'addedBy', 'AddedBy', 'userName', 'UserName')),
    enabled: Boolean(pick(value, 'enabled', 'Enabled')),
    active: Boolean(pick(value, 'active', 'Active')),
    usageNote: toNullableString(pick(value, 'usageNote', 'UsageNote')),
  }
}

function normalizeCargoTracking(value: unknown): CargoTracking | null {
  if (!asRecord(value)) return null

  return {
    orderNumber: toString(pick(value, 'orderNumber', 'OrderNumber')),
    deliveryNumber: toString(pick(value, 'deliveryNumber', 'DeliveryNumber')),
    statusCode: toNumber(pick(value, 'statusCode', 'StatusCode')),
    statusDescription: toNullableString(pick(value, 'statusDescription', 'StatusDescription')),
    cargoNumber: toNullableString(pick(value, 'cargoNumber', 'CargoNumber')),
    cargoEventDescription: toNullableString(pick(value, 'cargoEventDescription', 'CargoEventDescription')),
    cargoExplanation: toNullableString(pick(value, 'cargoExplanation', 'CargoExplanation')),
    arrivalCenter: toNullableString(pick(value, 'arrivalCenter', 'ArrivalCenter')),
    documentId: toNullableString(pick(value, 'documentId', 'DocumentId')),
    deliveryWhen: toNullableString(pick(value, 'deliveryWhen', 'DeliveryWhen')),
    receiverName: toNullableString(pick(value, 'receiverName', 'ReceiverName')),
    receiverAddress: toNullableString(pick(value, 'receiverAddress', 'ReceiverAddress')),
    trackingUrl: normalizeYurticiTrackingUrl(pick(value, 'trackingUrl', 'TrackingUrl')),
  }
}

function normalizeSerialNumberCheck(value: unknown): SerialNumberCheck {
  return {
    serialNumber: toNullableString(pick(value, 'serialNumber', 'SerialNumber')),
    productCode: toNullableString(pick(value, 'productCode', 'ProductCode')),
    productName: toNullableString(pick(value, 'productName', 'ProductName')),
    brand: toNullableString(pick(value, 'brand', 'Brand')),
    category: toNullableString(pick(value, 'category', 'Category')),
    invoiceNumber: toNullableString(pick(value, 'invoiceNumber', 'InvoiceNumber')),
    eInvoiceNumber: toNullableString(pick(value, 'eInvoiceNumber', 'EInvoiceNumber')),
    invoiceDate: toNullableString(pick(value, 'invoiceDate', 'InvoiceDate')),
    deliveryNumber: toNullableString(pick(value, 'deliveryNumber', 'DeliveryNumber')),
    deliveryCreationDate: toNullableString(pick(value, 'deliveryCreationDate', 'DeliveryCreationDate')),
  }
}

function normalizeGuaranteeDetail(value: unknown): GuaranteeDetail | null {
  if (!asRecord(value)) return null

  return {
    serialNumber: toString(pick(value, 'serialNumber', 'SerialNumber')),
    productCode: toNullableString(pick(value, 'productCode', 'ProductCode')),
    productName: toNullableString(pick(value, 'productName', 'ProductName')),
    warrantyStatus: toNullableString(pick(value, 'warrantyStatus', 'WarrantyStatus')),
    brand: toNullableString(pick(value, 'brand', 'Brand')),
    invoiceNumber: toNullableString(pick(value, 'invoiceNumber', 'InvoiceNumber')),
    readingDate: toNullableString(pick(value, 'readingDate', 'ReadingDate')),
    categoryCode: toNullableString(pick(value, 'categoryCode', 'CategoryCode')),
  }
}

function normalizeBalance(value: unknown): ChatBalance {
  return {
    currency: normalizeCurrency(pick(value, 'currency', 'Currency', 'DOVIZ_KODU')),
    amount: toNumber(pick(value, 'amount', 'Amount', 'BAKIYE')),
    label: toNullableString(pick(value, 'label', 'Label')),
    isActive: Boolean(pick(value, 'isActive', 'IsActive') ?? true),
  }
}

function normalizeMoney(value: unknown): ChatMoney {
  return {
    currency: normalizeCurrency(pick(value, 'currency', 'Currency', 'DOVIZ_KODU')),
    amount: toNumber(pick(value, 'amount', 'Amount', 'TUTAR')),
  }
}

function normalizeDebtStatus(value: unknown): ChatDebtStatus | null {
  if (!asRecord(value)) return null
  const rawCompanies = pick(value, 'companies', 'Companies')
  if (!Array.isArray(rawCompanies) || rawCompanies.length === 0) return null

  const rawTotal = pick(value, 'total', 'Total')

  return {
    companies: rawCompanies.map(company => {
      const rawAmounts = pick(company, 'amounts', 'Amounts')
      return {
        companyCode: toString(pick(company, 'companyCode', 'CompanyCode', 'SIRKET_KODU')),
        companyName: toNullableString(pick(company, 'companyName', 'CompanyName', 'SIRKET_ADI')),
        amounts: Array.isArray(rawAmounts) ? rawAmounts.map(normalizeMoney) : [],
        detail: toNullableString(pick(company, 'detail', 'Detail')),
      }
    }),
    total: asRecord(rawTotal) ? normalizeMoney(rawTotal) : null,
    totalLabel: toNullableString(pick(value, 'totalLabel', 'TotalLabel')),
  }
}

/** Faz-43: Avantaj/Fırsat POS tanımları; tanımsız türdeki ya da boş şirketler düşer. */
function normalizePosLimits(value: unknown): ChatPosLimits | null {
  if (!asRecord(value)) return null
  const rawCompanies = pick(value, 'companies', 'Companies')
  if (!Array.isArray(rawCompanies)) return null

  const companies = rawCompanies
    .map(company => {
      const rawLines = pick(company, 'lines', 'Lines')
      const lines = (Array.isArray(rawLines) ? rawLines : [])
        .map(line => {
          const type = toString(pick(line, 'type', 'Type')).toLowerCase()
          if (type !== 'avantaj' && type !== 'firsat') return null
          return {
            type: type as 'avantaj' | 'firsat',
            typeLabel: toString(pick(line, 'typeLabel', 'TypeLabel'), type === 'avantaj' ? 'Avantaj POS' : 'Fırsat POS'),
            name: toNullableString(pick(line, 'name', 'Name')),
            limit: toNumber(pick(line, 'limit', 'Limit')),
            used: toNumber(pick(line, 'used', 'Used')),
            remaining: toNumber(pick(line, 'remaining', 'Remaining')),
          }
        })
        .filter((line): line is NonNullable<typeof line> => line !== null)
      return {
        companyCode: toString(pick(company, 'companyCode', 'CompanyCode')),
        companyName: toNullableString(pick(company, 'companyName', 'CompanyName')),
        lines,
      }
    })
    .filter(company => company.lines.length > 0)

  if (companies.length === 0) return null
  return { companies, canPay: pick(value, 'canPay', 'CanPay') === true }
}

function normalizeInvoice(value: unknown): ChatInvoice {
  return {
    invoiceNumber: toString(pick(value, 'invoiceNumber', 'InvoiceNumber', 'FaturaNo', 'FATURA_BELGENO')),
    invoiceDate: toNullableString(pick(value, 'invoiceDate', 'InvoiceDate', 'FaturaTarihi', 'FATURA_TARIHI')),
    dueDate: toNullableString(pick(value, 'dueDate', 'DueDate', 'VadeTarihi', 'VADE_TARIHI')),
    amount: toNumber(pick(value, 'amount', 'Amount', 'ToplamTry', 'NET_TUTAR')),
    currency: normalizeCurrency(pick(value, 'currency', 'Currency', 'BelgeParaBirimi', 'DOVIZ_KODU')),
    companyName: toNullableString(pick(value, 'companyName', 'CompanyName', 'Company')),
    eInvoiceNumber: toNullableString(pick(value, 'eInvoiceNumber', 'EInvoiceNumber', 'EFATNO')),
  }
}

function normalizeProductCampaign(value: unknown): ChatProductCampaign | null {
  if (!asRecord(value)) return null

  const label = toNullableString(pick(value, 'label', 'Label', 'productListCode', 'ProductListCode'))
  const description = toNullableString(
    pick(value, 'description', 'Description', 'productDetailDesc', 'ProductDetailDesc', 'campaignDesc', 'CampaignDesc')
  )
  const highlight = toNullableString(
    pick(value, 'highlight', 'Highlight', 'productDetailBoldDesc', 'ProductDetailBoldDesc')
  )
  if (!label && !description && !highlight) return null

  return {
    id: toNullableNumber(pick(value, 'id', 'Id', 'campaignId', 'CampaignId')),
    label,
    description,
    highlight,
  }
}

const CHAT_ADD_BLOCKS: readonly string[] = ['notInCatalog', 'iquote', 'kaspersky', 'noPrice', 'noStock']

/** Yalnız bilinen gerekçe kodları taşınır; bilinmeyen kod not üretmesin diye düşer. */
function normalizeAddBlock(value: unknown): ChatAddBlock | undefined {
  return typeof value === 'string' && CHAT_ADD_BLOCKS.includes(value) ? (value as ChatAddBlock) : undefined
}

function normalizeProduct(value: unknown): ChatProduct {
  const rawSpecs = pick(value, 'specs', 'Specs')
  const rawCampaigns = pick(value, 'campaigns', 'Campaigns', 'productCampaigns', 'ProductCampaigns')

  return {
    code: toString(pick(value, 'code', 'Code', 'ProductCode', 'URUN_KODU')),
    name: toString(pick(value, 'name', 'Name', 'ProductName', 'URUN_ACIKLAMA')),
    companyId: toNullableNumber(pick(value, 'companyId', 'CompanyId', 'CompanyID')),
    campaigns: Array.isArray(rawCampaigns)
      ? rawCampaigns
          .map(normalizeProductCampaign)
          .filter((campaign): campaign is ChatProductCampaign => campaign !== null)
      : undefined,
    brand: toNullableString(pick(value, 'brand', 'Brand', 'MARKA')),
    category: toNullableString(pick(value, 'category', 'Category', 'KATEGORI')),
    price: toNullableNumber(pick(value, 'price', 'Price', 'FIYAT')),
    currency: normalizeCurrency(pick(value, 'currency', 'Currency', 'DOVIZ_KODU')),
    stock: toNullableNumber(pick(value, 'stock', 'Stock', 'STOK')),
    imageUrl: toNullableString(pick(value, 'imageUrl', 'ImageUrl', 'Image')),
    productId: toNullableNumber(pick(value, 'productId', 'ProductId', 'Id')),
    description: toNullableString(pick(value, 'description', 'Description')),
    statBadge: toNullableString(pick(value, 'statBadge', 'StatBadge')),
    nameOnly: Boolean(pick(value, 'nameOnly', 'NameOnly')),
    specs: Array.isArray(rawSpecs)
      ? rawSpecs
          .map(spec => ({
            label: toString(pick(spec, 'label', 'Label', 'Key', 'Name')),
            value: toString(pick(spec, 'value', 'Value')),
          }))
          .filter(spec => spec.label && spec.value)
      : undefined,
    // Faz-29 Artım A. Eksik alanlar undefined kalır: mevcut sözleşme testleri toEqual ile
    // karşılaştırıyor ve undefined alanı yok sayıyor.
    addable: pick(value, 'addable', 'Addable') === true ? true : undefined,
    addBlock: normalizeAddBlock(pick(value, 'addBlock', 'AddBlock')),
    opportunityId: toNullableNumber(pick(value, 'opportunityId', 'OpportunityId')) ?? undefined,
  }
}

const LEGACY_BRAND_COMPANIES: Record<number, ChatBrandCompany> = {
  1: { id: 1, code: '1000', name: 'Index' },
  2: { id: 2, code: '2300', name: 'Netex' },
  3: { id: 3, code: '1200', name: 'Despec' },
  4: { id: 4, code: '1100', name: 'Datagate' },
}

function normalizeBrandCompany(value: unknown): ChatBrandCompany | null {
  if (!asRecord(value)) {
    const legacyId = toNullableNumber(value)
    return legacyId === null ? null : LEGACY_BRAND_COMPANIES[legacyId] ?? null
  }

  const name = toNullableString(pick(value, 'name', 'Name'))
  if (!name) return null

  return {
    id: toNullableNumber(pick(value, 'id', 'Id', 'companyId', 'CompanyId')),
    code: toNullableString(pick(value, 'code', 'Code', 'companyCode', 'CompanyCode')),
    name,
  }
}

function normalizeBrand(value: unknown): ChatBrand | null {
  if (!asRecord(value)) return null

  const name = toNullableString(pick(value, 'name', 'Name'))
  const rawCompanies = pick(value, 'companies', 'Companies')
  if (!name || !Array.isArray(rawCompanies)) return null

  const companiesByKey = new Map<string, ChatBrandCompany>()
  rawCompanies
    .map(normalizeBrandCompany)
    .filter((company): company is ChatBrandCompany => company !== null)
    .forEach(company => {
      const key = `${company.id ?? ''}:${company.code ?? ''}:${company.name.toLocaleLowerCase('tr-TR')}`
      companiesByKey.set(key, company)
    })
  const companies = Array.from(companiesByKey.values())
  if (companies.length === 0) return null

  return {
    id: toNullableNumber(pick(value, 'id', 'Id')),
    name,
    imageUrl: toNullableString(pick(value, 'imageUrl', 'ImageUrl', 'image', 'Image')),
    companies,
  }
}

/** Yol doğrulamasında kullanılan kukla taban; üretilen mutlak adres kullanılmaz, yalnız origin karşılaştırılır. */
const CATEGORY_PATH_BASE = 'https://category-path.invalid'

/**
 * Chip yolları sunucuda beyaz listeye göre üretilir; widget adres kurmaz, yalnız doğrular. Yol göreli
 * olmalı (`/` ile başlar) ve tarayıcının URL çözümleyicisinde origin'i değiştirmemeli.
 *
 * Prefix kontrolü tek başına yetmez: çözümleyici girdideki TAB/LF/CR karakterlerini silerek çözer,
 * yani "/\n/evil.com" → "//evil.com" olup protokol-göreli mutlak adrese kaçar. Origin karşılaştırması
 * bunu da, ters bölülü ("/\evil.com") ve mutlak ("https://…") biçimleri de tek kuralla yakalar.
 *
 * `normalizeNavigationAction` sabit portal yolları için tam eşleşme beyaz listesi kullanır; kategori yolları
 * açık uçlu olduğu için buradaki mekanizma zorunlu olarak daha zayıftır — niyet aynı, garanti değil.
 */
function normalizeCategoryPath(value: unknown): string | null {
  const path = toNullableString(value)
  if (!path || !path.startsWith('/')) return null

  try {
    // Origin değiştiyse yol site dışına çıkıyor → chip pasif kalır.
    return new URL(path, CATEGORY_PATH_BASE).origin === CATEGORY_PATH_BASE ? path : null
  } catch {
    return null // çözümlenemeyen yola güvenilmez
  }
}

function normalizeCategory(value: unknown): ChatCategory | null {
  if (!asRecord(value)) return null

  const categoryId = toNullableNumber(pick(value, 'categoryId', 'CategoryId'))
  const name = toNullableString(pick(value, 'name', 'Name'))
  // Kimliksiz veya adsız kayıt chip olamaz; bu bir doğrulama değil, boş kaydı elemedir.
  if (categoryId === null || !name) return null

  // Backend'in adet eşlemesi güvenilir değil (çoğunlukla null gelir). 0/negatif rozet bilgi
  // taşımaz ve "0 yazmayın" kuralına girer → rozetsiz duruma indirilir. Kesirli (3.7) veya
  // boolean kökenli (true → 1) bir sayı gerçek bir adet değildir, o da rozetsiz duruma indirilir.
  const productCount = toNullableNumber(pick(value, 'productCount', 'ProductCount'))

  return {
    categoryId,
    name,
    productCount: productCount !== null && Number.isInteger(productCount) && productCount > 0 ? productCount : null,
    path: normalizeCategoryPath(pick(pick(value, 'openAction', 'OpenAction'), 'path', 'Path')),
  }
}

function normalizeRepresentativeContact(value: unknown): ChatRepresentativeContact | null {
  if (!asRecord(value)) return null

  const type = toNullableString(pick(value, 'type', 'Type'))
  if (!type) return null

  const rawPhoneNumbers = pick(value, 'phoneNumbers', 'PhoneNumbers')
  const phoneNumbers = Array.isArray(rawPhoneNumbers)
    ? Array.from(
        new Set(
          rawPhoneNumbers.map(phone => toNullableString(phone)).filter((phone): phone is string => phone !== null)
        )
      )
    : []

  return {
    type,
    name: toNullableString(pick(value, 'name', 'Name')),
    email: toNullableString(pick(value, 'email', 'Email')),
    phoneNumbers,
  }
}

function normalizeRepresentativeCompany(value: unknown): ChatRepresentativeCompany | null {
  if (!asRecord(value)) return null

  const companyName = toNullableString(pick(value, 'companyName', 'CompanyName'))
  const rawContacts = pick(value, 'contacts', 'Contacts')
  const contacts = Array.isArray(rawContacts)
    ? rawContacts
        .map(normalizeRepresentativeContact)
        .filter((contact): contact is ChatRepresentativeContact => contact !== null)
    : []

  if (!companyName || contacts.length === 0) return null

  return {
    companyId: toNullableNumber(pick(value, 'companyId', 'CompanyId', 'CompanyID')),
    companyCode: toNullableString(pick(value, 'companyCode', 'CompanyCode')),
    companyName,
    contacts,
  }
}

/**
 * Sunucunun üretebileceği sabit portal yolları — tam eşleşme beyaz listesi. Yol backend'de sabittir;
 * widget adres kurmaz, yalnız bu kümeyle karşılaştırır. `/myspecial/mypayments` ödeme EKRANINA
 * yönlendirmedir (ödeme işlemi değil) ve sunucu yalnız yetkili kullanıcıya gönderir.
 */
const PORTAL_NAVIGATION_PATHS = new Set([
  '/myspecial/Password',
  '/myspecial/UserUpdate',
  '/myspecial/AddNewUser',
  '/myspecial/mypayments',
  // Faz-30: sohbetten sepet işlemi sonrası "Sepete git" / "Siparişi tamamla".
  '/mycard',
  // Faz-34: XML destek araçları (getXmlBilgi / getXmlDurumum / getXmlStok) → "XML Destek".
  '/myspecial/xmlSupport',
  // Faz-35: B2B menü yönlendirmeleri (getSeriNoKontrol, imeiSorgula, siparisIptalIade).
  '/myspecial/ssh/SerialNumberCheck',
  '/myspecial/reports/imei-serial-number',
  '/myspecial/myorders',
  // Faz-39: bayi SSS — banka hesapları sayfası ve Fırsat Pazarı (kutu hasarlı ürünler) listesi.
  '/financial-information/bank-account-numbers',
  '/products?specialFilter=opportunit-market',
  // Faz-40: alisverisKredisi → "Seri No ile Kredi Kullandırma" (yalnız COMPAY_KREDI alt tipli bayiye gönderilir).
  '/myspecial/serial-credit-application',
  // Faz-48: odemeGunu → "Ödeme günü bilgilerinizi buradan öğrenebilirsiniz" (Açık Faturalar sayfası).
  '/myspecial/reports/open-invoices',
])

/**
 * Kurumsal site alan adları — backend'in `CorporateLinks.IsAllowed` beyaz listesinin aynası.
 * Widget adres KURMAZ; sunucudan gelen adres burada da doğrulanır ki tek bir sızıntı yeterli
 * olmasın. Alt alan adı kabul edilir, sorgu dizesi kabul edilmez (izleme parametresi taşımasın).
 */
const CORPORATE_LINK_DOMAINS = [
  'indexgrup.com',
  'index.com.tr',
  'datagate.com.tr',
  'despec.com.tr',
  'netex.com.tr',
  'teklos.com.tr',
]

/**
 * Faz-39: portalda yayımlanan yardım dokümanları — tam eşleşme beyaz listesi (backend `PortalDocuments`
 * aynası). `indexgruppazar.com` kurumsal alan adı listesine EKLENMEZ; yalnız bu adresler geçer.
 */
const PORTAL_DOCUMENT_URLS = new Set([
  'https://www.indexgruppazar.com/content/images/uploadedDocs/aciklama.jpeg',
  'https://www.indexgruppazar.com/content/documents/uploadedDocs/kullan_c_ekleme-_g_ncelleme_dok_man.pdf',
])

function normalizeExternalLink(value: unknown): ChatExternalLink | null {
  if (!asRecord(value)) return null

  const label = toNullableString(pick(value, 'label', 'Label'))
  const url = toNullableString(pick(value, 'url', 'Url'))
  if (!label || !url) return null
  if (PORTAL_DOCUMENT_URLS.has(url)) return { label, url }

  let parsed: URL
  try {
    parsed = new URL(url)
  } catch {
    return null // çözümlenemeyen adres bağlantıya çevrilmez
  }
  if (parsed.protocol !== 'https:' || parsed.search) return null

  const host = parsed.hostname.toLowerCase()
  const allowed = CORPORATE_LINK_DOMAINS.some(domain => host === domain || host.endsWith(`.${domain}`))
  return allowed ? { label, url } : null
}

function normalizeNavigationAction(value: unknown): ChatNavigationAction | null {
  if (!asRecord(value)) return null

  const label = toNullableString(pick(value, 'label', 'Label'))
  const path = toNullableString(pick(value, 'path', 'Path'))
  if (!label || !path || !PORTAL_NAVIGATION_PATHS.has(path)) return null

  return { label, path }
}

/**
 * Sunucunun gönderebileceği video adresleri — tam eşleşme beyaz listesi. Widget adres kurmaz ve
 * serbest URL kabul etmez; beyaz liste dışındaki değer sessizce elenir (yeni video eklenirken
 * backend sabiti ile bu küme birlikte güncellenir).
 */
const PROMO_VIDEO_URLS = new Set([
  'https://www.indexgruppazar.com/content/videos/uploadedDocs/IndexGrupPazar_iGini.mp4',
])

/** Kapak görseli yalnız kendi statik içerik kökümüzden kabul edilir (uzak takip pikseli olmasın). */
const PROMO_POSTER_PREFIXES = ['https://www.indexgruppazar.com/content/', '/images/']

function normalizePromoVideo(value: unknown): ChatPromoVideo | null {
  if (!asRecord(value)) return null

  const label = toNullableString(pick(value, 'label', 'Label'))
  const url = toNullableString(pick(value, 'url', 'Url'))
  if (!label || !url || !PROMO_VIDEO_URLS.has(url)) return null

  const posterUrl = toNullableString(pick(value, 'posterUrl', 'PosterUrl'))
  const safePoster = posterUrl && PROMO_POSTER_PREFIXES.some(prefix => posterUrl.startsWith(prefix)) ? posterUrl : null

  return {
    title: toNullableString(pick(value, 'title', 'Title')) ?? label,
    label,
    url,
    posterUrl: safePoster,
  }
}

function normalizeProductSearch(value: unknown): ChatProductSearch | null {
  if (!asRecord(value)) return null

  const keyword = toString(pick(value, 'keyword', 'Keyword')).trim()
  const shownCount = toNumber(pick(value, 'shownCount', 'ShownCount'))
  const totalCount = toNumber(pick(value, 'totalCount', 'TotalCount'))
  // Anahtar kelime olmadan ürün listesi açılamaz; sayılar tutarsızsa "Tümünü Getir" gösterilmez.
  if (!keyword || shownCount <= 0) return null

  return { keyword, shownCount, totalCount }
}

function normalizeTable(value: unknown): ChatTable | null {
  if (!asRecord(value)) return null
  const rawColumns = pick(value, 'columns', 'Columns')
  const rawRows = pick(value, 'rows', 'Rows')
  if (!Array.isArray(rawColumns) || rawColumns.length === 0 || !Array.isArray(rawRows)) return null

  return {
    title: toNullableString(pick(value, 'title', 'Title')),
    columns: rawColumns.map(column => toString(column)),
    rows: rawRows.filter(Array.isArray).map(row => (row as unknown[]).map(cell => toString(cell))),
    groupFirstColumn: Boolean(pick(value, 'groupFirstColumn', 'GroupFirstColumn')),
  }
}

/** Düz string→string sözlüğü (form bağlam parametreleri); boş/geçersizse undefined. */
function normalizeStringMap(value: unknown): Record<string, string> | undefined {
  if (!asRecord(value)) return undefined
  const result: Record<string, string> = {}
  for (const [key, raw] of Object.entries(value as Record<string, unknown>)) {
    const text = toString(raw).trim()
    if (key && text) result[key] = text
  }
  return Object.keys(result).length > 0 ? result : undefined
}

function normalizeForm(value: unknown): ChatForm | null {
  if (!asRecord(value)) return null
  const rawOptions = pick(value, 'options', 'Options')
  if (!Array.isArray(rawOptions) || rawOptions.length === 0) return null

  return {
    intent: toNullableString(pick(value, 'intent', 'Intent')) ?? undefined,
    paramKey: toNullableString(pick(value, 'paramKey', 'ParamKey')) ?? undefined,
    params: normalizeStringMap(pick(value, 'params', 'Params')),
    stepLabel: toNullableString(pick(value, 'stepLabel', 'StepLabel')) ?? undefined,
    stepCurrent: toNumber(pick(value, 'stepCurrent', 'StepCurrent')) || undefined,
    stepTotal: toNumber(pick(value, 'stepTotal', 'StepTotal')) || undefined,
    percent: toNumber(pick(value, 'percent', 'Percent')) || undefined,
    prompt: toNullableString(pick(value, 'prompt', 'Prompt')) ?? undefined,
    options: rawOptions.map(option => ({
      value: toString(pick(option, 'value', 'Value')),
      label: toString(pick(option, 'label', 'Label')),
      description: toNullableString(pick(option, 'description', 'Description')) ?? undefined,
      intent: toNullableString(pick(option, 'intent', 'Intent')) ?? undefined,
    })),
  }
}

function normalizeBasketProposalLine(value: unknown): ChatBasketProposalLine {
  return {
    name: toString(pick(value, 'name', 'Name')),
    code: toString(pick(value, 'code', 'Code')),
    companyId: toNullableNumber(pick(value, 'companyId', 'CompanyId')),
    imageUrl: toNullableString(pick(value, 'imageUrl', 'ImageUrl')),
    quantity: toNumber(pick(value, 'quantity', 'Quantity'), 1),
    unitPrice: toNullableNumber(pick(value, 'unitPrice', 'UnitPrice')),
    currency: normalizeCurrency(pick(value, 'currency', 'Currency')) as ChatCurrency,
    note: toNullableString(pick(value, 'note', 'Note')),
    previousQuantity: toNullableNumber(pick(value, 'previousQuantity', 'PreviousQuantity')) ?? undefined,
  }
}

function normalizeBasketProposalExcluded(value: unknown): ChatBasketProposalExcluded {
  return {
    name: toString(pick(value, 'name', 'Name')),
    reason: toString(pick(value, 'reason', 'Reason')),
    note: toString(pick(value, 'note', 'Note')),
  }
}

/** Faz-30 sepet önerisi kartı; satırı ve eleneni olmayan kart çizilmez. */
function normalizeBasketProposal(value: unknown): ChatBasketProposal | undefined {
  if (!asRecord(value)) return undefined
  const rawLines = pick(value, 'lines', 'Lines')
  const rawExcluded = pick(value, 'excluded', 'Excluded')
  const lines = Array.isArray(rawLines) ? rawLines.map(normalizeBasketProposalLine) : []
  const excluded = Array.isArray(rawExcluded) ? rawExcluded.map(normalizeBasketProposalExcluded) : []
  if (lines.length === 0 && excluded.length === 0) return undefined
  const moreLineCount = toNumber(pick(value, 'moreLineCount', 'MoreLineCount'), 0)
  // Faz-32: sipariş tekrarı kartının başlığı; geçmişten tek ürün önerisinde gelmez.
  const orderNo = toNullableString(pick(value, 'orderNo', 'OrderNo'))
  const orderDate = toNullableString(pick(value, 'orderDate', 'OrderDate'))
  return {
    op: toString(pick(value, 'op', 'Op'), 'add'),
    lines,
    excluded,
    moreLineCount: moreLineCount > 0 ? moreLineCount : undefined,
    orderNo: orderNo ?? undefined,
    orderDate: orderDate ?? undefined,
  }
}

function normalizeCommissionMode(value: unknown): CommissionMode {
  return toString(value).toLowerCase() === 'all' ? 'all' : 'best'
}

function normalizeCommissionPaymentType(value: unknown): ChatCommissionPaymentType {
  return {
    code: toString(pick(value, 'code', 'Code')).trim(),
    name: toString(pick(value, 'name', 'Name')).trim(),
    isDefault: Boolean(pick(value, 'isDefault', 'IsDefault')),
  }
}

/**
 * Sepet komisyon kartı. Kapsamı belirsiz satır karta yalan söyletir: kapsam kodu boşsa,
 * taksit tam sayı değilse/1'in altındaysa veya oran sayısal değilse satır elenir. Gerçek %0
 * (komisyonsuz kampanya) geçerlidir ve elenmez. Sekmesiz kapsam ve kapsamsız sekme de elenir;
 * hiç geçerli satır kalmazsa kart çizilmez, bot metni kalır.
 */
function normalizeBasketCommissionRates(value: unknown): ChatBasketCommissionRates | null {
  if (!asRecord(value)) return null

  const rawRates = pick(value, 'rates', 'Rates')
  if (!Array.isArray(rawRates)) return null

  const parsedRates = rawRates.map(rate => ({
    scopeCode: toString(pick(rate, 'scopeCode', 'ScopeCode')).trim(),
    installment: toNumber(pick(rate, 'installment', 'Installment')),
    interestRate: toNullableNumber(pick(rate, 'interestRate', 'InterestRate')),
  }))

  const isValidRate = (
    rate: (typeof parsedRates)[number]
  ): rate is (typeof parsedRates)[number] & { interestRate: number } =>
    Boolean(rate.scopeCode) &&
    Number.isInteger(rate.installment) &&
    rate.installment >= 1 &&
    rate.interestRate !== null

  const rates: ChatBasketRateRow[] = parsedRates.filter(isValidRate)
  if (rates.length === 0) return null

  const rawTabs = pick(value, 'tabs', 'Tabs')
  const tabs: ChatBasketRateTab[] = (Array.isArray(rawTabs) ? rawTabs : [])
    .map(tab => ({
      code: toString(pick(tab, 'code', 'Code')).trim(),
      name: toString(pick(tab, 'name', 'Name')).trim(),
      isDefault: Boolean(pick(tab, 'isDefault', 'IsDefault')),
    }))
    .filter(tab => tab.code && tab.name)

  const scopeCodes = new Set(rates.map(rate => rate.scopeCode))
  const tabCodes = new Set(tabs.map(tab => tab.code))
  const rawScopes = pick(value, 'scopes', 'Scopes')
  const scopes: ChatBasketRateScope[] = (Array.isArray(rawScopes) ? rawScopes : [])
    .map(scope => ({
      code: toString(pick(scope, 'code', 'Code')).trim(),
      tabCode: toString(pick(scope, 'tabCode', 'TabCode')).trim(),
      name: toString(pick(scope, 'name', 'Name')).trim(),
      isDefault: Boolean(pick(scope, 'isDefault', 'IsDefault')),
      companyNames: toNullableString(pick(scope, 'companyNames', 'CompanyNames')),
      startDate: toNullableString(pick(scope, 'startDate', 'StartDate')),
      endDate: toNullableString(pick(scope, 'endDate', 'EndDate')),
    }))
    .filter(scope => scope.code && scope.name && tabCodes.has(scope.tabCode) && scopeCodes.has(scope.code))

  if (scopes.length === 0) return null

  const usableTabs = tabs.filter(tab => scopes.some(scope => scope.tabCode === tab.code))
  if (usableTabs.length === 0) return null

  return {
    title: toNullableString(pick(value, 'title', 'Title')),
    note: toNullableString(pick(value, 'note', 'Note')),
    tabs: usableTabs,
    scopes,
    rates,
  }
}

function normalizeCommissionRates(value: unknown): ChatCommissionRates | null {
  if (!asRecord(value)) return null

  const rawRates = pick(value, 'rates', 'Rates')
  if (!Array.isArray(rawRates)) return null

  const parsedRates = rawRates.map(rate => ({
    companyCode: toString(pick(rate, 'companyCode', 'CompanyCode', 'Company')).trim(),
    paymentTypeCode: toString(pick(rate, 'paymentTypeCode', 'PaymentTypeCode', 'Type')).trim(),
    bankName: toString(pick(rate, 'bankName', 'BankName')).trim(),
    installment: toNumber(pick(rate, 'installment', 'Installment')),
    interestRate: toNullableNumber(pick(rate, 'interestRate', 'InterestRate')),
  }))

  const isValidRate = (
    rate: (typeof parsedRates)[number]
  ): rate is (typeof parsedRates)[number] & { interestRate: number } =>
    Boolean(rate.companyCode) &&
    Boolean(rate.paymentTypeCode) &&
    Boolean(rate.bankName) &&
    Number.isInteger(rate.installment) &&
    rate.installment >= 1 &&
    rate.interestRate !== null

  // Kapsamı belirsiz satır karta yalan söyletir: şirket/tip/banka boşsa, taksit tam sayı
  // değilse/1'in altındaysa veya oran sayısal değilse (null/eksik/"3,75") satır elenir.
  // Gerçek %0 oran (komisyonsuz kampanya) geçerlidir ve elenmez — toNullableNumber bu ikisini
  // ayırt eder. Hiç geçerli satır kalmazsa kart çizilmez, bot metni kalır.
  const rates: ChatCommissionRate[] = parsedRates.filter(isValidRate)
  if (rates.length === 0) return null

  const rawPaymentTypes = pick(value, 'paymentTypes', 'PaymentTypes')

  return {
    mode: normalizeCommissionMode(pick(value, 'mode', 'Mode')),
    note: toNullableString(pick(value, 'note', 'Note')),
    companies: normalizeCompanies(pick(value, 'companies', 'Companies')),
    paymentTypes: Array.isArray(rawPaymentTypes)
      ? rawPaymentTypes.map(normalizeCommissionPaymentType).filter(paymentType => paymentType.code && paymentType.name)
      : [],
    rates,
  }
}

export function normalizeChatMessageResponse(value: unknown): ChatMessageResponse {
  const rawOrders = pick(value, 'orders', 'Orders')
  const rawBalances = pick(value, 'balances', 'Balances')
  const rawInvoices = pick(value, 'invoices', 'Invoices')
  const rawProducts = pick(value, 'products', 'Products')
  const rawBrands = pick(value, 'brands', 'Brands')
  const rawCategories = pick(value, 'categories', 'Categories')
  const rawRepresentatives = pick(value, 'representatives', 'Representatives')
  const rawNavigationActions = pick(value, 'navigationActions', 'NavigationActions')
  const rawExternalLinks = pick(value, 'externalLinks', 'ExternalLinks')
  const rawSerialChecks = pick(value, 'serialNumberChecks', 'SerialNumberChecks')
  const rawBankAccounts = pick(value, 'bankAccounts', 'BankAccounts')
  const rawCoupons = pick(value, 'coupons', 'Coupons')
  const rawCreditCards = pick(value, 'creditCards', 'CreditCards')
  const rawAddresses = pick(value, 'addresses', 'Addresses')
  const rawServiceProducts = pick(value, 'serviceProducts', 'ServiceProducts')

  return {
    conversationId: toNumber(pick(value, 'conversationId', 'ConversationId')),
    // Geri bildirim çapası (ADR-015); alan yoksa 0 kalır ve çubuk çizilmez.
    messageId: toNumber(pick(value, 'messageId', 'MessageId')),
    text: toString(pick(value, 'text', 'Text'), 'Yanıt alınamadı.'),
    intent: toString(pick(value, 'intent', 'Intent')),
    toolUsed: toNullableString(pick(value, 'toolUsed', 'ToolUsed')),
    fromButton: Boolean(pick(value, 'fromButton', 'FromButton')),
    form: normalizeForm(pick(value, 'form', 'Form')),
    // Faz-30. Eksik alanlar undefined kalır (mevcut sözleşme testleri toEqual ile karşılaştırıyor).
    basketProposal: normalizeBasketProposal(pick(value, 'basketProposal', 'BasketProposal')),
    basketChanged: pick(value, 'basketChanged', 'BasketChanged') === true ? true : undefined,
    orders: Array.isArray(rawOrders) ? rawOrders.map(normalizeOrder) : null,
    balances: Array.isArray(rawBalances) ? rawBalances.map(normalizeBalance) : null,
    debtStatus: normalizeDebtStatus(pick(value, 'debtStatus', 'DebtStatus')),
    posLimits: normalizePosLimits(pick(value, 'posLimits', 'PosLimits')) ?? undefined,
    invoices: Array.isArray(rawInvoices) ? rawInvoices.map(normalizeInvoice) : null,
    products: Array.isArray(rawProducts) ? rawProducts.map(normalizeProduct) : null,
    productSearch: normalizeProductSearch(pick(value, 'productSearch', 'ProductSearch')),
    brands: Array.isArray(rawBrands)
      ? rawBrands.map(normalizeBrand).filter((brand): brand is ChatBrand => brand !== null)
      : null,
    categories: Array.isArray(rawCategories)
      ? rawCategories.map(normalizeCategory).filter((category): category is ChatCategory => category !== null)
      : null,
    representatives: Array.isArray(rawRepresentatives)
      ? rawRepresentatives
          .map(normalizeRepresentativeCompany)
          .filter((company): company is ChatRepresentativeCompany => company !== null)
      : null,
    navigationActions: Array.isArray(rawNavigationActions)
      ? rawNavigationActions
          .map(normalizeNavigationAction)
          .filter((action): action is ChatNavigationAction => action !== null)
      : null,
    externalLinks: Array.isArray(rawExternalLinks)
      ? rawExternalLinks.map(normalizeExternalLink).filter((link): link is ChatExternalLink => link !== null)
      : null,
    promoVideo: normalizePromoVideo(pick(value, 'promoVideo', 'PromoVideo')),
    table: normalizeTable(pick(value, 'table', 'Table')),
    cargoTracking: normalizeCargoTracking(pick(value, 'cargoTracking', 'CargoTracking')),
    serialNumberChecks: Array.isArray(rawSerialChecks) ? rawSerialChecks.map(normalizeSerialNumberCheck) : null,
    guaranteeDetail: normalizeGuaranteeDetail(pick(value, 'guaranteeDetail', 'GuaranteeDetail')),
    // IBAN'sız satır kart için anlamsız; bu bir doğrulama değil, boş kaydı elemedir.
    bankAccounts: Array.isArray(rawBankAccounts)
      ? rawBankAccounts.map(normalizeBankAccount).filter(account => account.iban)
      : null,
    // Kupon kodu olmayan satır kart için anlamsız (kopyalanacak değer yok) → elenir.
    coupons: Array.isArray(rawCoupons) ? rawCoupons.map(normalizeCoupon).filter(coupon => coupon.code) : null,
    // Maskeli numarası olmayan satır kart için anlamsız (gösterilecek kimlik yok) → elenir.
    creditCards: Array.isArray(rawCreditCards)
      ? rawCreditCards.map(normalizeSavedCreditCard).filter(card => card.maskedNumber)
      : null,
    // Adres metni olmayan satır kart için anlamsız; bu bir doğrulama değil, boş kaydı elemedir.
    addresses: Array.isArray(rawAddresses)
      ? rawAddresses.map(normalizeAddress).filter(address => address.addressLine)
      : null,
    commissionRates: normalizeCommissionRates(pick(value, 'commissionRates', 'CommissionRates')),
    basketCommissionRates: normalizeBasketCommissionRates(
      pick(value, 'basketCommissionRates', 'BasketCommissionRates')
    ),
    // Ürün adı olmayan satır kart için anlamsız (gösterilecek kimlik yok) → elenir.
    serviceProducts: Array.isArray(rawServiceProducts)
      ? rawServiceProducts.map(normalizeServiceProduct).filter(item => item.productName)
      : null,
  }
}

export function normalizeButtons(value: unknown): ButtonDefinition[] {
  if (!Array.isArray(value)) return []

  return value
    .map(button => ({
      Intent: toString(pick(button, 'intent', 'Intent')),
      Label: toString(pick(button, 'label', 'Label')),
      NeedsInput: Boolean(pick(button, 'needsInput', 'NeedsInput')),
      Group: toString(pick(button, 'group', 'Group')),
    }))
    .filter(button => button.Intent && button.Label)
}

export function normalizeCompanies(value: unknown): ChatCompany[] {
  if (!Array.isArray(value)) return []

  return value
    .map(company => ({
      code: toString(pick(company, 'Code', 'code')).trim(),
      name: toString(pick(company, 'Name', 'name')).trim(),
    }))
    .filter(company => company.code && company.name)
}
